import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Radio } from 'components/Form/Radio/Radio';
import { Spinner } from 'components/UI/Spinner/Spinner';
import React from 'react';

import classes from './RadioAvatarCard.module.scss';

interface RadioAvatarCardProps {
    checked?: boolean;
    alt?: string;
    avatar?: string;
    icon?: IconProp;
    label?: string;
    loading?: boolean;
    disabled?: boolean;
    className?: string;
    description?: string | React.ReactNode;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const getRadioAvatarCardState = (props: {
    checked?: boolean;
    loading?: boolean;
    disabled?: boolean;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
    const faded = Boolean(props.disabled);

    return {
        checked: Boolean(props.checked) && !props.disabled,
        inactive: Boolean(props.loading || props.disabled),
        faded,
        radioDisabled: faded,
        onChange: faded ? undefined : props.onChange
    };
};

export const RadioAvatarCard: React.FC<RadioAvatarCardProps> = props => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const state = getRadioAvatarCardState(props);

    const triggerChange = () => {
        if (state.inactive) {
            return;
        }

        if (inputRef.current) {
            inputRef.current.click();
        }
    };

    return (
        <div
            className={clsx(classes.root, props.className, {
                [classes.checked]: state.checked,
                [classes.disabled]: state.inactive,
                [classes.faded]: state.faded
            })}
            onClick={triggerChange}
        >
            <Radio
                ref={inputRef}
                checked={state.checked}
                disabled={state.radioDisabled}
                onChange={state.onChange}
                className={classes.input}
            />

            <div className={classes.avatarContainer}>
                {props.avatar && <img src={props.avatar} alt="avatar" className={classes.avatar} />}

                {props.icon && <FontAwesomeIcon icon={props.icon} className={classes.icon} />}
            </div>

            <div>
                {props.label && <p className={classes.label}>{props.label}</p>}
                {props.description && <p className={classes.description}>{props.description}</p>}
            </div>

            {props.loading && (
                <div className={classes.loading}>
                    <Spinner />
                </div>
            )}
        </div>
    );
};
